import type { CSSProperties } from 'react';
import { adminButton, adminFocusable, adminText, adminTokens as t } from '../../admin-ui';
import { createCenterstageStyles, type CenterstageStyles } from '../centerstage/centerstageStyles';

/**
 * The Question Bank browse surfaces (the page at /questions and the Author
 * with AI picker dialog): the Centerstage search pill, chips, concepts dialog
 * and panels, plus the bank's filter pills, sort row, question cards, detail
 * rows, the picker's modal frame, the page's header and taxonomy panel, and
 * the picker's "Skills & risk areas" pill with its popover.
 */

const MODAL_PAD = '22px';

const metaLabel: CSSProperties = {
  margin: 0,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: t.text.secondary,
};

const accentLink: CSSProperties = {
  ...adminFocusable,
  padding: 0,
  border: 'none',
  background: 'none',
  fontFamily: 'inherit',
  fontSize: t.typography.label.size,
  fontWeight: 600,
  color: t.brand.roseForeground,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export type QuestionBankBrowseStyleKey =
  | 'header'
  | 'headerText'
  | 'title'
  | 'close'
  | 'top'
  | 'findRow'
  | 'filters'
  | 'fieldPill'
  | 'fieldPillFocused'
  | 'fieldName'
  | 'fieldSelect'
  | 'fieldFixed'
  | 'fieldValue'
  | 'sortPill'
  | 'sortSelect'
  | 'sortRow'
  | 'count'
  | 'showAll'
  | 'note'
  | 'body'
  | 'list'
  | 'cardWrap'
  | 'card'
  | 'cardWithActions'
  | 'cardHover'
  | 'cardText'
  | 'cardQuestion'
  | 'cardMeta'
  | 'cardGlyph'
  | 'cardActions'
  | 'cardDetails'
  | 'cardDetailsHover'
  | 'cardSelect'
  | 'cardSelectHover'
  | 'cardError'
  | 'listFoot'
  | 'listFootPagination'
  | 'detailTitle'
  | 'detailBody'
  | 'detailIdentity'
  | 'detailRows'
  | 'detailRow'
  | 'detailLabel'
  | 'detailValue'
  | 'detailLink'
  | 'untagged'
  | 'detailFooter'
  | 'footerError'
  | 'secondaryButton'
  | 'primaryButton'
  | 'buttonDisabled'
  | 'pageHeader'
  | 'manageLink'
  | 'pageSortRow'
  | 'pageBody'
  | 'listColumn'
  | 'panel'
  | 'panelIntro'
  | 'panelSections'
  | 'panelSection'
  | 'panelHead'
  | 'panelHeadName'
  | 'panelChevron'
  | 'panelChevronOpen'
  | 'panelParents'
  | 'panelParentRow'
  | 'panelParentName'
  | 'panelParentNameOpen'
  | 'panelParentCount'
  | 'panelChildren'
  | 'panelOption'
  | 'panelCheckbox'
  | 'panelPlaceholderTitle'
  | 'panelPlaceholderBody'
  | 'panelLink'
  | 'taxonomyWrap'
  | 'taxonomyPill'
  | 'taxonomyPillOpen'
  | 'taxonomyPillNameOpen'
  | 'taxonomyCaret'
  | 'taxonomyCaretOpen'
  | 'taxonomyPopover';

export interface QuestionBankBrowseStyles {
  /** The shared Centerstage pieces (search pill, chips, concepts dialog, panels, page frame). */
  centerstage: CenterstageStyles;
  browse: Record<QuestionBankBrowseStyleKey, CSSProperties>;
}

/** The taxonomy panel column beside the page's list (writeup: 276px). */
export const QUESTION_BANK_PANEL_WIDTH = '276px';

/** A small chevron drawn from two borders, pointing down; rotated to point up when open. */
const chevron: CSSProperties = {
  flex: 'none',
  width: '6px',
  height: '6px',
  margin: '-3px 2px 0 0',
  borderRight: '1.5px solid currentColor',
  borderBottom: '1.5px solid currentColor',
  transform: 'rotate(45deg)',
};

export function createQuestionBankBrowseStyles({ narrow }: { narrow: boolean }): QuestionBankBrowseStyles {
  const base = createCenterstageStyles({ narrow });
  const centerstage: CenterstageStyles = {
    ...base,
    findBar: { ...base.findBar, flexWrap: 'nowrap' },
    searchRow: { ...base.searchRow, flex: '1 1 auto' },
  };
  // A filter pill: the field name outside the control, so the open menu lists plain values.
  const pill: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: t.spacing.xs,
    height: '44px',
    padding: `0 ${t.spacing.md} 0 ${t.spacing.lg}`,
    borderRadius: t.radius.pill,
    border: `1px solid ${t.border.control}`,
    backgroundColor: t.surface.panel,
    boxSizing: 'border-box',
    fontFamily: t.typography.fontFamily,
    fontSize: t.typography.body.size,
    color: t.text.primary,
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  };

  return {
    centerstage,
    browse: {
      // The picker's header: title and close only, vertically centred, 18px above and below (Sara, 2026-11-04).
      header: {
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: `18px ${MODAL_PAD}`,
        borderBottom: `1px solid ${t.border.subtle}`,
        fontFamily: t.typography.fontFamily,
      },
      headerText: { flex: 1, minWidth: 0 },
      title: { ...adminText.pageTitle, fontSize: '22px' },
      close: { ...adminButton.icon, flexShrink: 0 },

      // Find bar and sort row: fixed above the scrolling list.
      top: {
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: `16px ${MODAL_PAD} 14px`,
        borderBottom: `1px solid ${t.border.subtle}`,
        backgroundColor: t.surface.panel,
        fontFamily: t.typography.fontFamily,
      },
      // The pills drop under the search when the row is too narrow for both, and wrap among themselves after that
      // (review, 2026-11-03: the picker's 90vw frame at 900px clipped the Skills & risk areas pill).
      findRow: { display: 'flex', alignItems: 'center', gap: t.spacing.md, flexWrap: 'wrap' },
      filters: { display: 'flex', alignItems: 'center', gap: t.spacing.sm, flexWrap: 'wrap' },
      fieldPill: pill,
      // The pill shows keyboard focus (FilterSelect); the select's own ring would be a second box, and
      // Chrome draws it after every mouse pick.
      fieldPillFocused: base.searchPillFocused,
      fieldName: { color: t.text.secondary, fontSize: t.typography.label.size, fontWeight: 600 },
      fieldSelect: {
        outline: 'none',
        height: '100%',
        padding: 0,
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: t.typography.body.size,
        fontWeight: 600,
        color: t.text.primary,
        cursor: 'pointer',
      },
      // With nothing to choose between, the control states a fact rather than offering a choice.
      fieldFixed: { ...pill, backgroundColor: t.surface.subtle },
      fieldValue: { fontWeight: 600 },
      // Smaller and squarer than the Type and Purpose pills, so it reads as a control without competing with them.
      sortPill: {
        ...pill,
        height: '32px',
        padding: `0 ${t.spacing.sm} 0 ${t.spacing.md}`,
        borderRadius: t.radius.control,
        backgroundColor: 'transparent',
        fontSize: t.typography.label.size,
      },
      sortSelect: {
        outline: 'none',
        height: '100%',
        padding: 0,
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: t.typography.label.size,
        color: t.text.primary,
        cursor: 'pointer',
      },
      sortRow: { display: 'flex', alignItems: 'center', gap: t.spacing.md, flexWrap: 'wrap', minHeight: '36px' },
      count: { marginLeft: 'auto', fontSize: t.typography.label.size, color: t.text.secondary, whiteSpace: 'nowrap' },
      showAll: accentLink,
      note: { margin: 0, fontSize: t.typography.label.size, color: t.text.secondary },

      // The list
      body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: t.spacing.md,
        padding: `16px ${MODAL_PAD} ${MODAL_PAD}`,
        backgroundColor: t.surface.canvas,
        fontFamily: t.typography.fontFamily,
      },
      list: { display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, padding: 0, listStyle: 'none' },
      // Holds the card button and, in the picker, the controls placed over its right edge.
      cardWrap: { position: 'relative' },
      card: {
        ...adminFocusable,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: t.spacing.md,
        padding: '14px 16px',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: t.text.primary,
        backgroundColor: t.surface.panel,
        border: `1px solid ${t.border.subtle}`,
        borderRadius: t.radius.tile,
        boxShadow: t.shadow.panel,
        cursor: 'pointer',
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
      },
      // Room at the right for Details and Select, held whether or not they show.
      cardWithActions: { paddingRight: '156px' },
      cardHover: { boxShadow: t.shadow.overlay, border: `1px solid ${t.border.control}` },
      cardText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
      cardQuestion: { fontSize: '16px', fontWeight: 600, lineHeight: 1.4 },
      // Smaller and lighter than the label token, off-token by the PO's request (2026-11-03).
      cardMeta: { fontSize: '11.5px', color: '#8C88A3' },
      cardGlyph: { flexShrink: 0, color: t.text.secondary, fontSize: '16px' },
      // The picker's card controls (Sara, 2026-11-04): Details then Select, 12px apart, over the card's right edge on
      // the question's first line, fading in over 120ms on hover and focus-within.
      cardActions: {
        position: 'absolute',
        top: '12px',
        right: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transition: 'opacity 120ms ease',
      },
      // Plain accent text with a faint underline that solidifies on hover: a label for the card's own click.
      cardDetails: {
        fontSize: '12.5px',
        color: t.brand.roseForeground,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        textDecorationColor: 'rgba(212, 0, 106, 0.35)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      },
      cardDetailsHover: { textDecorationColor: t.brand.roseForeground },
      // A bordered pill on the panel fill, filling with the accent colour and white text on hover.
      cardSelect: {
        ...adminFocusable,
        padding: '5px 14px',
        borderRadius: t.radius.pill,
        border: `1px solid ${t.border.control}`,
        backgroundColor: t.surface.panel,
        color: t.text.primary,
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      },
      cardSelectHover: { backgroundColor: t.brand.roseForeground, border: '1px solid transparent', color: t.text.onPrimary },
      // The server's rejection of a Select, under that card.
      cardError: { margin: 0, padding: '6px 16px 0', fontSize: t.typography.caption.size, lineHeight: t.typography.caption.lineHeight, color: t.status.error.text },
      // The row under the cards: the pagination, and at its right whatever the caller puts there.
      listFoot: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: t.spacing.md, flexWrap: 'wrap' },
      listFootPagination: { flex: 1, minWidth: 0 },

      // The detail dialog
      detailTitle: { ...adminText.sectionTitle, fontSize: '19px', lineHeight: 1.35, flex: 1, minWidth: 0 },
      detailBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: t.spacing.lg,
        padding: `${t.spacing.lg} ${t.spacing.xl} ${t.spacing.xl}`,
        fontFamily: t.typography.fontFamily,
        color: t.text.primary,
      },
      detailIdentity: { margin: 0, fontSize: t.typography.label.size, color: t.text.secondary },
      detailRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: t.spacing.md,
        margin: 0,
        padding: `${t.spacing.lg} 0 0`,
        borderTop: `1px solid ${t.border.subtle}`,
      },
      detailRow: {
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '168px 1fr',
        gap: t.spacing.sm,
        margin: 0,
        alignItems: 'baseline',
      },
      detailLabel: metaLabel,
      detailValue: { margin: 0, fontSize: t.typography.body.size, lineHeight: 1.5 },
      detailLink: {
        ...accentLink,
        fontSize: t.typography.body.size,
        textAlign: 'left',
        whiteSpace: 'normal',
      },
      untagged: { margin: 0, fontSize: t.typography.body.size, color: t.text.secondary },
      detailFooter: base.dialogFooter,
      footerError: { margin: 0, marginRight: 'auto', flex: '1 1 240px', fontSize: t.typography.label.size, color: t.status.error.text },
      secondaryButton: adminButton.secondary,
      primaryButton: adminButton.primary,
      buttonDisabled: adminButton.disabled,

      // The page: header with the Manage questions link, find row, sort row, list beside the panel.
      pageHeader: { display: 'flex', alignItems: 'flex-start', gap: t.spacing.lg, flexWrap: 'wrap' },
      // Quiet, at the right of the pagination row under the list (Sara, 2026-11-04): not something to lead a demo with.
      manageLink: { flexShrink: 0, fontSize: t.typography.label.size, color: t.text.secondary, textDecoration: 'underline', textUnderlineOffset: '2px', whiteSpace: 'nowrap', padding: '4px 2px' },
      pageSortRow: {
        display: 'flex',
        alignItems: 'center',
        gap: `${t.spacing.sm} ${t.spacing.md}`,
        flexWrap: 'wrap',
        minHeight: '36px',
        paddingBottom: t.spacing.md,
        borderBottom: `1px solid ${t.border.subtle}`,
      },
      pageBody: {
        display: 'flex',
        flexDirection: narrow ? 'column-reverse' : 'row',
        alignItems: narrow ? 'stretch' : 'flex-start',
        gap: '26px',
        fontFamily: t.typography.fontFamily,
      },
      listColumn: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: t.spacing.md },

      // The taxonomy panel: scrolls with the list, sized to its content.
      panel: {
        width: narrow ? 'auto' : QUESTION_BANK_PANEL_WIDTH,
        flex: 'none',
        boxSizing: 'border-box',
        padding: `${t.spacing.lg} 15px 10px`,
        backgroundColor: t.surface.panel,
        border: `1px solid ${t.border.subtle}`,
        borderRadius: t.radius.control,
        fontFamily: t.typography.fontFamily,
        color: t.text.primary,
      },
      panelIntro: { margin: `0 0 20px`, fontSize: t.typography.caption.size, fontWeight: 600, lineHeight: 1.45 },
      // The sections share one gap, so the last ends flush with the panel's or the popover's bottom padding.
      panelSections: { display: 'flex', flexDirection: 'column', gap: t.spacing.xl },
      panelSection: { margin: 0 },
      // The whole heading toggles every parent in the section.
      panelHead: {
        ...adminFocusable,
        display: 'flex',
        alignItems: 'center',
        gap: t.spacing.sm,
        width: '100%',
        padding: '6px 10px',
        margin: `0 0 ${t.spacing.xs}`,
        border: 'none',
        borderRadius: '4px',
        backgroundColor: t.surface.subtle,
        fontFamily: 'inherit',
        textAlign: 'left',
        color: t.text.secondary,
        cursor: 'pointer',
      },
      panelHeadName: { ...metaLabel, flex: 1, fontSize: t.typography.caption.size },
      panelChevron: chevron,
      panelChevronOpen: { transform: 'rotate(-135deg)', margin: '2px 2px 0 0' },
      panelParents: { margin: 0, padding: '0 0 0 13px', listStyle: 'none' },
      panelParentRow: {
        ...adminFocusable,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: t.spacing.sm,
        padding: '5px 0',
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: '13.5px',
        lineHeight: 1.35,
        textAlign: 'left',
        color: t.text.primary,
        cursor: 'pointer',
      },
      panelParentName: { flex: 1, minWidth: 0 },
      panelParentNameOpen: { fontWeight: 600 },
      panelParentCount: {
        fontSize: '11.5px',
        lineHeight: '16px',
        padding: '0 6px',
        borderRadius: t.radius.pill,
        backgroundColor: t.surface.subtle,
        color: t.text.secondary,
      },
      panelChildren: { display: 'flex', flexDirection: 'column', padding: '1px 0 6px 2px' },
      panelOption: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '9px',
        padding: `${t.spacing.xs} 0`,
        fontSize: '12.5px',
        lineHeight: 1.35,
        cursor: 'pointer',
      },
      panelCheckbox: { margin: '1px 0 0', flex: 'none', width: '13px', height: '13px', accentColor: t.brand.roseForeground },
      panelPlaceholderTitle: { margin: '0 0 7px', fontSize: t.typography.caption.size, fontWeight: 600, lineHeight: 1.45 },
      panelPlaceholderBody: { margin: '0 0 11px', fontSize: t.typography.caption.size, lineHeight: 1.45, color: t.text.secondary },
      panelLink: { ...accentLink, fontSize: '12.5px', whiteSpace: 'normal', textAlign: 'left' },

      // The picker's "Skills & risk areas" pill and the popover under it: the page's sections,
      // since the dialog has no room for the panel column (prototype: 300px, right-aligned to the pill).
      taxonomyWrap: { flex: 'none', display: 'inline-flex' },
      taxonomyPill: { ...pill, ...adminFocusable, gap: '7px', cursor: 'pointer' },
      // The whole border, not borderColor: the pill's base sets the shorthand, and React warns when the two mix.
      taxonomyPillOpen: { border: `1px solid ${t.brand.roseForeground}`, color: t.brand.roseForeground },
      taxonomyPillNameOpen: { color: t.brand.roseForeground },
      taxonomyCaret: { ...chevron, margin: '-3px 0 0 1px', color: t.text.secondary },
      taxonomyCaretOpen: { transform: 'rotate(-135deg)', margin: '2px 0 0 1px', color: t.brand.roseForeground },
      // Placed against the viewport (top, right and max height set from the pill's position when it opens),
      // so the dialog's overflow clipping cannot cut it short when the list is shorter than the popover.
      taxonomyPopover: {
        position: 'fixed',
        zIndex: 40,
        width: '300px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        padding: '14px 15px 10px',
        backgroundColor: t.surface.panel,
        border: `1px solid ${t.border.subtle}`,
        borderRadius: '10px',
        boxShadow: t.shadow.overlay,
        fontFamily: t.typography.fontFamily,
        color: t.text.primary,
        textAlign: 'left',
      },
    },
  };
}
